/**
 * stepPlayer — one fixed physics tick for the player capsule.
 *
 * Pure function of (state, input, field, dt): no clocks, no randomness, no allocation.
 * The client, the test harness, and a future server-side validator all replay ticks
 * bit-identically. All movement-DETERMINING logic lives here (gravity, jump buffer/coyote,
 * direction smoothing, freeze rule); the client keeps only presentation (interpolation,
 * camera) and input latching.
 *
 * The unknown-space rule (the "never end up in ungenerated chunks" guarantee):
 *  - If the chunks required for VERTICAL simulation (support under the capsule, step/snap
 *    margins) are unknown → the tick is skipped entirely: position and velocity bitwise
 *    unchanged, fall/coyote timers paused, `frozen` reported. Streaming latency therefore
 *    suspends the player instead of dropping them.
 *  - If only the HORIZONTAL destination is unknown → the horizontal displacement is dropped
 *    for this tick (`blockedByUnknown`) and vertical simulation proceeds. The streaming
 *    frontier acts as a wall you can stand at and walk away from — never a floor you can
 *    stand on and never a void you can enter.
 */
import { COYOTE_TIME, DIRECTION_SMOOTH_SPEED, GRAVITY, JUMP_BUFFER_TIME, JUMP_VELOCITY, MAX_SNAP_DISTANCE, MOVE_SPEED, PLAYER_HEIGHT_INNER, PLAYER_RADIUS, SPRINT_MULTIPLIER, STEP_HEIGHT, TERMINAL_VELOCITY, } from '../protocol/physics.js';
import { lerpAngle, rotateToWorldDirection } from '../util/movement.js';
import { depenetrate, moveCapsule, verifyCapsule, } from './capsule.js';
/** The player capsule (eye at the top of the axis, feet R below the axis bottom). */
export const PLAYER_HULL = {
    radius: PLAYER_RADIUS,
    innerHeight: PLAYER_HEIGHT_INNER,
};
/**
 * How completely two consecutive ticks must cancel to count as oscillation rather than a
 * turn. `|a + b| < FRAC * (|a| + |b|)` — 0 would demand an exact reversal, 1 would match any
 * direction change at all. R56 measures judder with the same definition.
 */
const OSC_CANCEL_FRAC = 0.4;
// Region margins for the unknown-space checks (see module doc)
const REGION_HORIZ_MARGIN = PLAYER_RADIUS + 0.15;
const REGION_UP_BASE = PLAYER_RADIUS + STEP_HEIGHT + 0.1;
const REGION_DOWN_BASE = PLAYER_RADIUS + MAX_SNAP_DISTANCE + 0.2;
// Module scratch (single-threaded, non-reentrant)
const io = {
    x: 0, y: 0, z: 0,
    grounded: false,
    landed: false, hitCeiling: false, wallContact: false, steppedUp: false, stepAdvance: 0,
    groundNormalY: 1, groundNx: 1, groundNz: 0,
};
/**
 * Fraction of the commanded distance each tick that goes to repaying step-debt. 1 would freeze
 * the player at the top of every riser until the debt cleared (a visible hitch); 0 re-opens the
 * staircase speed exploit. Half reads as "climbing slows you", repays a full landing advance
 * (≤ R + skin ≈ 0.27 m) in ~14 ticks (~120 ms), and keeps repayment strictly faster than the
 * ~26-tick riser cadence of a 1 m staircase, so debt cannot accumulate across steps.
 */
const STEP_DEBT_PAY_FRAC = 0.5;
/** Fresh player state at an eye position (treated as valid until proven otherwise). */
export function createPlayerState(x, y, z) {
    return {
        x, y, z,
        vx: 0, vy: 0, vz: 0,
        grounded: false,
        coyote: 0,
        jumpBuffer: 0,
        smoothDirAngle: 0,
        wasMoving: false,
        fallTime: 0,
        frozen: false,
        frozenMs: 0,
        stuck: false,
        lastValidX: x, lastValidY: y, lastValidZ: z,
        lastGroundX: x, lastGroundY: y, lastGroundZ: z,
        hasLastGround: false,
        prevMoveX: 0, prevMoveZ: 0,
        stepDebt: 0,
        prevCmdX: 0, prevCmdZ: 0,
        groundNormalY: 1, groundNx: 1, groundNz: 0,
    };
}
/** Hard teleport: move the eye, zero motion, clear transient flags and anchors. */
export function teleportPlayer(state, x, y, z) {
    state.x = x;
    state.y = y;
    state.z = z;
    state.vx = 0;
    state.vy = 0;
    state.vz = 0;
    state.grounded = false;
    state.coyote = 0;
    state.jumpBuffer = 0;
    state.fallTime = 0;
    state.frozen = false;
    state.frozenMs = 0;
    state.stuck = false;
    state.wasMoving = false;
    state.lastValidX = x;
    state.lastValidY = y;
    state.lastValidZ = z;
    state.stepDebt = 0;
}
/**
 * Advance the player one fixed tick. Mutates `state`; writes observables to `out`.
 */
export function stepPlayer(state, input, field, dt, out) {
    out.grounded = state.grounded;
    out.frozen = false;
    out.blockedByUnknown = false;
    out.stuck = false;
    out.steppedUp = false;
    out.hitCeiling = false;
    out.wallContact = false;
    out.jumped = false;
    out.depenetrations = 0;
    // ---- Non-finite guard: restore the last verified pose rather than propagating NaN ----
    if (!Number.isFinite(state.x) || !Number.isFinite(state.y) || !Number.isFinite(state.z) ||
        !Number.isFinite(state.vx) || !Number.isFinite(state.vy) || !Number.isFinite(state.vz)) {
        state.x = state.lastValidX;
        state.y = state.lastValidY;
        state.z = state.lastValidZ;
        state.vx = 0;
        state.vy = 0;
        state.vz = 0;
    }
    const prevX = state.x;
    const prevY = state.y;
    const prevZ = state.z;
    // ---- Unknown-space rule, vertical requirement: freeze the whole tick ----
    const vdyPlanned = state.vy * dt;
    const top = state.y + REGION_UP_BASE + (vdyPlanned > 0 ? vdyPlanned : 0);
    const bottom = state.y - PLAYER_HEIGHT_INNER - REGION_DOWN_BASE + (vdyPlanned < 0 ? vdyPlanned : 0);
    if (!field.isRegionKnown(state.x - REGION_HORIZ_MARGIN, bottom, state.z - REGION_HORIZ_MARGIN, state.x + REGION_HORIZ_MARGIN, top, state.z + REGION_HORIZ_MARGIN)) {
        state.frozen = true;
        state.frozenMs += dt * 1000;
        out.frozen = true;
        return; // position, velocity, and all timers untouched
    }
    if (state.frozen) {
        state.frozen = false;
        state.frozenMs = 0;
    }
    // ---- Jump buffer + coyote (legacy semantics, tick units) ----
    if (input.jumpPressed || input.jumpHeld)
        state.jumpBuffer = JUMP_BUFFER_TIME;
    if ((state.grounded || state.coyote > 0) && state.jumpBuffer > 0) {
        state.vy = JUMP_VELOCITY;
        state.grounded = false;
        state.coyote = 0;
        state.jumpBuffer = 0;
        out.jumped = true;
    }
    state.jumpBuffer -= dt;
    if (!state.grounded)
        state.coyote -= dt;
    // ---- Gravity + terminal velocity ----
    if (state.grounded) {
        state.vy = 0;
    }
    else {
        state.vy += GRAVITY * dt;
        if (state.vy < -TERMINAL_VELOCITY)
            state.vy = -TERMINAL_VELOCITY;
    }
    // ---- Horizontal input (ported verbatim from the legacy PlayerLocal) ----
    let dispX = 0;
    let dispZ = 0;
    const moveMag = Math.min(1, Math.sqrt(input.moveX * input.moveX + input.moveZ * input.moveZ));
    if (moveMag > 0) {
        const dir = rotateToWorldDirection(input.moveX, input.moveZ, input.yaw);
        const targetAngle = Math.atan2(dir.worldX, dir.worldZ);
        if (!state.wasMoving) {
            state.smoothDirAngle = targetAngle; // snap on movement start for responsiveness
        }
        else {
            const smoothFactor = 1 - Math.exp(-DIRECTION_SMOOTH_SPEED * dt);
            state.smoothDirAngle = lerpAngle(state.smoothDirAngle, targetAngle, smoothFactor);
        }
        let speed = MOVE_SPEED * moveMag;
        if (input.sprint)
            speed *= SPRINT_MULTIPLIER;
        // Walk at `speed` ALONG THE GROUND rather than along the horizontal plane. Holding the
        // horizontal component at full speed whatever the grade makes along-surface speed
        // MOVE_SPEED/cos(g) and the climb rate MOVE_SPEED*tan(g), which is unbounded: measured, the
        // player ascended a 50° slope at 7.15 m/s — faster than their own 6 m/s walk — with
        // instantaneous peaks of 9-20 m/s against a 15 m/s jump.
        //
        // Scaled by cos(grade) only in proportion to how much of the move actually heads UPHILL.
        // Scaling the whole horizontal vector instead also slows a contour walk across a hillside,
        // where nothing is being climbed at all (R35), and starves the roll of the push it needs to
        // mount a ledge (R28, R34). Flat ground and any downhill or across-slope heading are
        // untouched; only the uphill component pays.
        if (state.grounded && state.groundNormalY < 0.999) {
            const dh = Math.hypot(state.groundNx, state.groundNz);
            if (dh > 1e-6) {
                // (groundNx, groundNz) points downhill, so uphill is its negation.
                const ux = -state.groundNx / dh;
                const uz = -state.groundNz / dh;
                let up = Math.sin(state.smoothDirAngle) * ux + Math.cos(state.smoothDirAngle) * uz;
                if (up < 0)
                    up = 0;
                else if (up > 1)
                    up = 1;
                speed *= 1 - (1 - state.groundNormalY) * up;
            }
        }
        dispX = Math.sin(state.smoothDirAngle) * speed * dt;
        dispZ = Math.cos(state.smoothDirAngle) * speed * dt;
        state.wasMoving = true;
    }
    else {
        state.wasMoving = false;
    }
    // ---- Step-debt repayment: a step-up granted distance the tick never paid for; walking it
    // off (never more than half the commanded pace, so a step reads as effort, not a freeze)
    // is what keeps stairs from out-running flat ground (R34). Grounded only — air control is
    // not the place to collect.
    if (state.stepDebt > 0 && state.grounded && (dispX !== 0 || dispZ !== 0)) {
        const d = Math.hypot(dispX, dispZ);
        const pay = Math.min(state.stepDebt, d * STEP_DEBT_PAY_FRAC);
        const scale = (d - pay) / d;
        dispX *= scale;
        dispZ *= scale;
        state.stepDebt -= pay;
    }
    // ---- Unknown-space rule, horizontal requirement: drop motion into unknown ----
    if (dispX !== 0 || dispZ !== 0) {
        const minX = Math.min(state.x, state.x + dispX) - REGION_HORIZ_MARGIN;
        const maxX = Math.max(state.x, state.x + dispX) + REGION_HORIZ_MARGIN;
        const minZ = Math.min(state.z, state.z + dispZ) - REGION_HORIZ_MARGIN;
        const maxZ = Math.max(state.z, state.z + dispZ) + REGION_HORIZ_MARGIN;
        if (!field.isRegionKnown(minX, bottom, minZ, maxX, top, maxZ)) {
            dispX = 0;
            dispZ = 0;
            out.blockedByUnknown = true;
        }
    }
    // ---- Collision solve ----
    io.x = state.x;
    io.y = state.y;
    io.z = state.z;
    io.grounded = state.grounded;
    io.landed = false;
    io.hitCeiling = false;
    io.wallContact = false;
    io.steppedUp = false;
    io.stepAdvance = 0;
    io.groundNormalY = 1;
    io.groundNx = 1;
    io.groundNz = 0;
    moveCapsule(field, PLAYER_HULL, io, dispX, state.vy * dt, dispZ);
    if (io.stepAdvance > 0)
        state.stepDebt += io.stepAdvance;
    // NO downhill snap. This used to reach ~1.5 ticks of horizontal travel (54 mm at a walk)
    // below the feet and place the capsule on whatever it found, to stop stairs bunny-bouncing.
    // Measured, that is what made the player NEVER leave the ground: 0 airborne ticks out of 96
    // both walking seeded terrain and walking DOWN a staircase, with the descent arriving as
    // `-54 -19 0 -24 -55 -52 -46` mm — a magnet re-grabbing at its own reach every tick, seven
    // direction reversals per step. Removing it, the same descent becomes a clean gravity arc
    // (`0 -3 -6 -8 -11 -14 ... -36`, airborne 48/96, zero reversals), which is what the mesh-BVH
    // engine did: it had no support query at all, only push-out, and push-out cannot pull down.
    //
    // Leaving the ground on a downslope is therefore CORRECT, not a bug to be suppressed. If
    // bunny-bounce ever reappears, fix it where it belongs — landing should absorb, not be
    // prevented — and never by reaching down and grabbing the ground again.
    state.x = io.x;
    state.y = io.y;
    state.z = io.z;
    state.grounded = io.grounded;
    state.groundNormalY = io.groundNormalY;
    state.groundNx = io.groundNx;
    state.groundNz = io.groundNz;
    out.steppedUp = io.steppedUp;
    out.hitCeiling = io.hitCeiling;
    out.wallContact = io.wallContact;
    if (io.hitCeiling && state.vy > 0)
        state.vy = 0; // jump-cancel, never bounce
    if (state.grounded) {
        state.vy = 0;
        state.coyote = COYOTE_TIME;
    }
    // ---- Interior verify + depenetration ladder + restore ----
    if (!verifyCapsule(field, PLAYER_HULL, state.x, state.y, state.z)) {
        out.depenetrations = 1;
        if (!depenetrate(field, PLAYER_HULL, io)) {
            // Rung 3: restore the last verified pose
            io.x = state.lastValidX;
            io.y = state.lastValidY;
            io.z = state.lastValidZ;
            if (!verifyCapsule(field, PLAYER_HULL, io.x, io.y, io.z)) {
                // Entombed even at the last valid pose — hold position, report stuck
                state.stuck = true;
                out.stuck = true;
            }
        }
        state.x = io.x;
        state.y = io.y;
        state.z = io.z;
        state.grounded = io.grounded;
        state.vy = 0;
        state.vx = 0;
        state.vz = 0;
    }
    if (!out.stuck)
        state.stuck = false; // stuck persists only while resolution keeps failing
    // ---- Bookkeeping ----
    if (!state.stuck) {
        state.lastValidX = state.x;
        state.lastValidY = state.y;
        state.lastValidZ = state.z;
    }
    if (state.grounded) {
        state.lastGroundX = state.x;
        state.lastGroundY = state.y;
        state.lastGroundZ = state.z;
        state.hasLastGround = true;
    }
    if (!state.grounded && state.vy < 0) {
        state.fallTime += dt;
    }
    else {
        state.fallTime = 0;
    }
    // ---- Oscillation damper ----
    //
    // An inside corner has no fixed point for a collide-and-slide solver. Each tick it picks
    // whichever wall is nearer and slides along it, which carries the capsule across the
    // bisector into the other wall, so the next tick slides the opposite way — forever. No
    // individual step is illegal, which is why every attempt to fix this inside the solver
    // failed: measured, a multi-plane crease rule, Quake's primal-velocity guard and 6x the
    // solver iterations were all byte-identical no-ops.
    //
    // So catch the SYMPTOM instead of trying to classify the geometry. If a tick undoes the
    // previous one while pressed against a wall, and the player did not ask to turn around,
    // it was oscillation rather than travel: drop it. The capsule then holds still in the
    // corner, which is what walking into a dead end should do.
    //
    // Keying on motion rather than on shape is what makes this safe. A curved wall — every dug
    // room and tunnel in the game — turns the travel direction gradually, so consecutive ticks
    // keep a positive dot and this never fires; an earlier attempt that reasoned about contact
    // PLANES could not tell a concave wall from a corner and stopped the player dead in both.
    // Measured, this changes travel along dug rooms, cave tubes, slopes and stairs by 0.0%,
    // while taking corner judder to zero at every angle and approach.
    if (out.wallContact && out.depenetrations === 0 && !out.stuck) {
        const moveX = state.x - prevX;
        const moveZ = state.z - prevZ;
        // A buzz is two consecutive ticks that CANCEL. Merely pointing the other way is not
        // enough and testing for that was measurably wrong twice: traversing a faceted wall
        // legitimately zigzags, and circling inside a cave tube swings the output direction past
        // 90° every tick without the player ever reversing — a plain dot-product test ate a third
        // of the travel there. Requiring the pair to sum to near nothing keeps sharp turns and
        // catches only motion that goes nowhere.
        const sum = Math.hypot(moveX + state.prevMoveX, moveZ + state.prevMoveZ);
        const parts = Math.hypot(moveX, moveZ) + Math.hypot(state.prevMoveX, state.prevMoveZ);
        const cancels = parts > 0 && sum < parts * OSC_CANCEL_FRAC;
        // ...and only when the player did NOT ask to turn around, because reversing into a wall
        // you are leaning on must stay instant. A wall is not a place to add input lag.
        const playerTurned = state.prevCmdX * dispX + state.prevCmdZ * dispZ < 0;
        if (cancels && !playerTurned) {
            state.x = prevX;
            state.z = prevZ;
        }
        else if (moveX !== 0 || moveZ !== 0) {
            state.prevMoveX = moveX;
            state.prevMoveZ = moveZ;
        }
    }
    if (dispX !== 0 || dispZ !== 0) {
        state.prevCmdX = dispX;
        state.prevCmdZ = dispZ;
    }
    // Observed velocity (includes pushes/snaps) — for animation/net consumers
    const invDt = 1 / dt;
    state.vx = (state.x - prevX) * invDt;
    state.vz = (state.z - prevZ) * invDt;
    out.grounded = state.grounded;
    void prevY;
}
//# sourceMappingURL=playerStepper.js.map